import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { CrDataWarning } from '@chancenwerk/client/components/chancen-component-data-warning.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { DurchfuehrungsTag, Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { PreferredSubjectAspect } from '@chancenwerk/shared/master-data-management/users/entities/aspects/PreferredSubjectAspect.js';
import { StudentTypeAspect } from '@chancenwerk/shared/master-data-management/users/entities/aspects/StudentTypeAspect.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { switchMap, takeUntil, tap } from 'rxjs';
import '../components/board-component-user-pool-card.js';
import { BoardDialogAddKPF } from '../components/dialogs/board-dialog-add-kpf.js';
import { dirtyData } from '../globalSubjects.js';
import { BoardRoutes } from '../routes.js';

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  _class: 1,
  kuerzel: 1,
  school: { name: 1, kuerzel: 1 },
  schoolID: 1,
  produkt: { ...Product.allFields, ...Product.capacitySelctionSet() },
};

const courseSelector: BaseQLSelectionSet<CourseDigital> = {
  _id: 1,
  _class: 1,
  name: 1,
  kpfID: 1,
  kpf: kpfSelector,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
  jahrgangsstufe: 1,
  changeNotification: 1,
  subject: 1,
  diKuIDs: 1,
  diKus: { _id: 1, _class: 1, vorname: 1, nachname: 1 },
  linkedKPFIDs: 1,
  linkedKPFs: kpfSelector,
  schuKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  leKos: { _id: 1, _class: 1, vorname: 1, username: 1, nachname: 1, email: 1 },
  osStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    inactive: 1,
    guestAccount: 1,
    email: 1,
    schuelertyp: 1,
    preferedFirstSubject: 1,
    preferedSecondSubject: 1,
    preferedThirdSubject: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kpfs: { school: { name: 1 } },
    kuendigung: { austrittsdatum: 1 },
  },
  usStudents: {
    _id: 1,
    _class: 1,
    vorname: 1,
    nachname: 1,
    username: 1,
    inactive: 1,
    guestAccount: 1,
    email: 1,
    schuelertyp: 1,
    preferedFirstSubject: 1,
    preferedSecondSubject: 1,
    preferedThirdSubject: 1,
    schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
    kuendigung: { austrittsdatum: 1 },
    kpfs: { school: { name: 1 } },
  },
};

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  vorname: 1,
  username: 1,
  inactive: 1,
  nachname: 1,
  email: 1,
  guestAccount: 1,
  schuelertyp: 1,
  preferedFirstSubject: 1,
  preferedSecondSubject: 1,
  preferedThirdSubject: 1,
  schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
  kpfs: { schoolID: 1, school: { name: 1 } },
  kuendigung: { austrittsdatum: 1 },
  lernfoerderungDuerchfuehrungsTagChoosen: DurchfuehrungsTag.allFields,
};

const diKuSelector: BaseQLSelectionSet<Mitarbeiter> = { _id: 1, _class: 1, vorname: 1, nachname: 1 };

/**
 * @customElement board-page-course-details-digital-intensive-course
 */
@customElement('board-page-course-details-digital-intensive-course')
export class BoardPageCourseDetailsIntensiveCourse extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'digitaler Intensivkurs';
    this.backRoute = BoardRoutes.digital;
  }

  @property() public digitalCourseID = new RXController(this, '');

  @state() private readonly _course = new RXController<Maybe<CourseDigital>>(this, null);

  @state() private readonly _kpfs = new RXController(
    this,
    this._course.observable.pipe(
      switchMap(async c => {
        return c?.linkedKPFs;
      }),
    ),
  );

  @state() private readonly _selectedKpf = new RXController<KPF | null>(this, null);

  @state() private readonly _kpfStudentsOS = new RXController(
    this,
    this._selectedKpf.observable.pipe(
      switchMap(kpf => {
        if (!kpf) return [];
        return UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [Student._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new KPFAspect({ value: kpf._id }),
              new StudentTypeAspect({ value: StudentType.OS }),
            ],
          }),
        )(studentSelector);
      }),
    ),
  );

  @state() private readonly _openStudentsOS = new RXController(
    this,
    this._course.observable.pipe(
      switchMap(course => {
        if (!course) {
          return [];
        }
        return UserAPI.subscribeByFilter(
          0,
          0,
          new ChancenwerkUserFilter({
            userClass: [Student._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new StudentTypeAspect({ value: StudentType.OS }),
              new IntAspect({
                fieldName: 'schoolInformation.jahrgangsstufe',
                min: course.jahrgangsstufe,
                max: course.jahrgangsstufe,
              }),
              new PreferredSubjectAspect({ value: course?.subject ? [course?.subject] : [] }),
            ],
          }),
        )(studentSelector);
      }),
    ),
  );

  @state() private readonly _diKus = new RXController(
    this,
    this._course.observable.pipe(
      switchMap(c =>
        UserAPI.subscribeByFilter(
          0,
          100, // TODO this is shit
          new ChancenwerkUserFilter({
            userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new ChancenRoleAspect({ value: Role.DKL }),
            ],
          }),
        )(diKuSelector),
      ),
    ),
  );

  override connectedCallback(): void {
    super.connectedCallback();

    this.digitalCourseID.observable.subscribe(async newId => {
      this._course.next(await CourseDigital.getByID<CourseDigital>(newId ?? '')(courseSelector));
    });

    this._course.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(c => {
          if (!c) {
            dirtyData.next([]);
            return;
          }

          dirtyData.next([c]);

          this.label = `digitaler Intensivkurs (${c?.jahrgangsstufe ? 'Jgst. ' + c?.jahrgangsstufe : ''} ${
            c?.subject
          })`;
        }),
      )
      .subscribe();
  }

  override header() {
    return html`<cr-flex horizontal space="sm">
      <cr-button
        mode="action"
        icon="save"
        @click=${async () => {
          if (this._course.value) {
            if (this._course.value.isDirty()) {
              this._course.value.changeNotification = true;
            }
            await ChromiumDialog.waitFor(this._course.value.save());
          }
        }}
      >
        Speichern
      </cr-button>
      ${this._checkNotification()}
    </cr-flex>`;
  }

  override content() {
    if (this._course.isLoading || !this._course.value)
      return html`
        <cr-flex center crossaxis-center>
          <cr-loader></cr-loader>
        </cr-flex>
      `;

    return html`<cr-flex space="md" padding="xs" horizontal>
      <cr-flex w100 space="md" ${css({ alignSelf: 'start' })}>
        <board-component-course-aggregated-detail-card
          ${css({ flex: 4 })}
          .expandable=${false}
          .dropzonesEnabled=${['DiKu', 'OS']}
          .coursesToManage=${[this._course.value]}
          .deleteDigital=${true}
          @deleted=${e => goTo(BoardRoutes.digital)}
          @dropped=${async () => {
            this.requestUpdate();

            if (!this._course.value) {
              return;
            }
            for (const u of this._course.value.osStudents) {
              for (const kpf of u.kpfs ?? []) {
                if (!this._course.value.linkedKPFIDs?.find(id => id === kpf._id)) {
                  this._course.value.linkedKPFIDs ??= [];

                  const fetched = await KPF.getByID<KPF>(kpf._id)(kpfSelector);
                  if (!fetched) {
                    continue;
                  }

                  this._course.value.linkedKPFIDs.push(kpf._id);
                  this._course.value.linkedKPFs = (this._course.value.linkedKPFs ?? []).concat([fetched]);
                  this._course.next(this._course.value);
                }
              }
            }
          }}
        >
        </board-component-course-aggregated-detail-card>
        <cr-card space="md" ${css({ marginTop: '20px' })}>
          <cr-flex
            space="sm"
            padding="sm"
            horizontal
            space-between
            ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)' })}
          >
            <cr-flex space="sm">
              <cr-h3>Teilnehmende KPFs</cr-h3>
              <cr-text>Gibt an aus welchen KPFs Nutzer bezogen werden</cr-text>
            </cr-flex>
            <cr-button icon="add" @click=${this._addKpf}>Hinzufügen</cr-button>
          </cr-flex>
          <cr-grid columns="0.33fr 0.33fr 0.33fr" space="md"> ${this._assignedKpfs()} </cr-grid>
        </cr-card>
      </cr-flex>
      ${this._studentSelection()}
    </cr-flex> `;
  }

  private _checkNotification() {
    if (this._course.value && (this._course.value.isDirty(true) || !this._course.value.changeNotification)) {
      return nothing;
    }

    return html` <cr-button
      icon="check"
      @click=${async () => {
        if (this._course.value) {
          this._course.value.changeNotification = false;
          await ChromiumDialog.waitFor(this._course.value.save());
        }
      }}
    >
      <cr-tooltip><cr-text>Änderung als angesehen abhacken.</cr-text></cr-tooltip>
      Angesehen
    </cr-button>`;
  }

  private _studentSelection() {
    return html`<board-component-user-pool-card width="450px">
      <x-flex slot="overTitle" ${css({ display: this._course.value?.isDirty() ? 'block' : 'none' })}>
        ${CrDataWarning(this._course.value?.isDirty())}
      </x-flex>

      <cr-dropdown-selection
        clearable
        .value=${this._selectedKpf.value}
        ?flex=${!this._selectedKpf.value}
        icon="search"
        placeholder="KPF"
        .selectables=${this._kpfs.value}
        .renderString=${(entry: Maybe<KPF>) => {
          if (!entry) return '';
          return entry.stringRepresentation;
        }}
        @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
          this._selectedKpf.next(e.detail.value);
        }}
      ></cr-dropdown-selection>
      <cr-flex space="sm">
        ${this._studentPools()}

        <chancen-component-card-title ${css({ marginTop: this.spacing.lg })}>
          ${this._countOpenStudents()} offene Jugendliche
        </chancen-component-card-title>
        ${this._openStudentPools()}

        <chancen-component-card-title ${css({ marginTop: this.spacing.lg })}>
          digitale Kursleitung
        </chancen-component-card-title>
        ${this._digitalPlanningPools()}
      </cr-flex>
    </board-component-user-pool-card>`;
  }

  private _studentPools() {
    return html`<board-component-user-pool
      label="Jugendliche"
      .kpfID=${this._selectedKpf.value?._id}
      .users=${this._filterAssignedFromUserPoolOS(this._selectedKpf.value ? (this._kpfStudentsOS.value ?? []) : [])}
      .userType=${'OS'}
    ></board-component-user-pool>`;
  }

  private _openStudentPools() {
    return html`<board-component-user-pool
      label="Zu verteilen"
      .users=${this._filterAssignedFromUserPoolOS(this._openStudentsOS.value ?? [])}
      .userType=${'OS'}
    ></board-component-user-pool>`;
  }

  private _digitalPlanningPools() {
    return html`<board-component-user-pool
      label="digitale Kursleitungen"
      .users=${this._filterAssignedFromUserPoolDiKu(this._diKus.value ? this._diKus.value : [])}
      .userType=${'DiKu'}
    ></board-component-user-pool>`;
  }

  private _countOpenStudents() {
    return (this._openStudentsOS.value ?? []).filter(student => {
      return !this._course.value?.osStudentIDs.some(id => id === student._id);
    }).length;
  }

  private _assignedKpfs() {
    if (!this._kpfs.value) {
      return nothing;
    }
    return html`${repeat(
      this._kpfs.value,
      kpf => kpf._id,
      kpf => {
        return html`<cr-card raised>
          <cr-flex horizontal start space-between>
            <cr-flex space="sm">
              <cr-text bold>${kpf.school?.name}</cr-text>
              <cr-text>${kpf.kuerzel}</cr-text>
            </cr-flex>
            <cr-flex horizontal space="sm">
              <cr-icon
                ${css({ color: this.colors.tone.error, cursor: 'pointer' })}
                @click=${async () => this._deleteKpf(kpf)}
                >delete</cr-icon
              >
            </cr-flex>
          </cr-flex>
        </cr-card>`;
      },
    )}`;
  }

  private async _addKpf() {
    const course = this._course.value;
    if (!course) return XSnackbar.show('Kein Kurs.');
    const newKpfId: string | null = await BoardDialogAddKPF.showPopup({
      props: { filterKpfs: this._course.value?.linkedKPFIDs },
    });

    if (newKpfId && newKpfId !== '') {
      const kpf = await KPF.getByID<KPF>(newKpfId)(kpfSelector);
      if (kpf) {
        course.linkedKPFIDs = (course.linkedKPFIDs ?? []).concat([newKpfId]);
        course.linkedKPFs = (course.linkedKPFs ?? []).concat([kpf]);
        this._course.next(this._course.value);
        this._selectedKpf.next(kpf);
      }
    }
  }

  private async _deleteKpf(kpf: KPF) {
    if (
      !(await ChromiumDialog.confirm(
        'Willst du dieses KPF wirklich entfernen? Dieser vorgang löscht auch zugehörige Schüler aus der Kurszuweisung.',
      ))
    )
      return;

    if (!this._course.value) {
      return;
    }

    if (this._selectedKpf.value?._id === kpf._id) {
      this._selectedKpf.next(null);
    }

    // Remove KPF from linked
    this._course.value.linkedKPFIDs = this._course.value.linkedKPFIDs?.filter(id => id !== kpf._id) ?? [];
    this._course.value.linkedKPFs = this._course.value.linkedKPFs?.filter(lkpf => lkpf._id !== kpf._id) ?? [];

    // Remove students that were assigned from that KPF
    const students = await Student.getByID<Student>(this._course.value.osStudentIDs)(studentSelector);
    this._course.value.osStudentIDs = this._course.value.osStudentIDs.filter(id => {
      const student = students.find(student => student._id === id);
      const inKpf = student?.kpfs?.some(skpf => {
        return skpf.schoolID === kpf.schoolID;
      });
      return !inKpf;
    });
    this._course.value.osStudents = this._course.value.osStudents.filter(student =>
      this._course.value?.osStudentIDs.some(sid => sid === student._id),
    );

    this._course.next(this._course.value);
    this.requestUpdate();
  }

  private _filterAssignedFromUserPoolOS(users: ChancenwerkUser[]) {
    return users.filter(user => !this._course.value?.osStudentIDs.some(u => u === user._id));
  }

  private _filterAssignedFromUserPoolDiKu(users: Mitarbeiter[]) {
    return users.filter(user => !this._course.value?.diKuIDs.some(u => u === user._id));
  }
}
